import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";

// Closing on backdrop click sounds simple, but naïve `onClick={onClose}` fires
// whenever `mouseup` lands on the backdrop — including drag-selection that
// starts inside an input and finishes outside. That kills the modal while the
// user thought they were just selecting text. We close only if BOTH mousedown
// AND the resulting click happened on the backdrop itself.
interface Props {
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

// Порог свайпа вниз для закрытия фуллскрин-модалки на мобиле.
const SWIPE_CLOSE_PX = 100;

export function ModalBackdrop({ onClose, children, className = "modal-backdrop" }: Props) {
  const downOnBackdrop = useRef(false);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    downOnBackdrop.current = e.target === e.currentTarget;
  };

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && downOnBackdrop.current) {
      onClose();
    }
    downOnBackdrop.current = false;
  };

  // Swipe-down-to-close: только на мобиле (фуллскрин-модалки) и только если
  // жест начался в зоне `.modal-head` — иначе обычный вертикальный скролл
  // внутри modal-body становился бы случайным закрытием.
  useEffect(() => {
    const backdrop = backdropRef.current;
    if (!backdrop) return;

    const isMobile = () => window.matchMedia("(max-width: 640px)").matches;

    const drag = {
      modal: null as HTMLElement | null,
      startY: 0,
      dy: 0,
      active: false,
    };

    const resetDrag = () => {
      drag.modal = null;
      drag.startY = 0;
      drag.dy = 0;
      drag.active = false;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!isMobile() || e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Interactive элементы в modal-head (.modal-close) — не перехватываем.
      if (target.closest("button, a, input, select, textarea, [contenteditable='true']")) return;
      const head = target.closest(".modal-head");
      if (!head) return;
      const modal = head.closest(".modal") as HTMLElement | null;
      if (!modal) return;
      drag.modal = modal;
      drag.startY = touch.clientY;
      drag.dy = 0;
      drag.active = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!drag.active || !drag.modal) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dy = touch.clientY - drag.startY;
      if (dy <= 0) {
        drag.modal.style.transform = "";
        drag.dy = 0;
        return;
      }
      // Блокируем нативный scroll страницы, чтобы жест читался как drag модалки.
      e.preventDefault();
      drag.dy = dy;
      drag.modal.style.transition = "none";
      drag.modal.style.transform = `translateY(${dy}px)`;
    };

    const onTouchEnd = () => {
      if (!drag.active || !drag.modal) return;
      const modal = drag.modal;
      if (drag.dy > SWIPE_CLOSE_PX) {
        modal.style.transition = "transform 180ms ease-out";
        modal.style.transform = "translateY(100vh)";
        window.setTimeout(onClose, 170);
      } else {
        modal.style.transition = "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)";
        modal.style.transform = "";
      }
      resetDrag();
    };

    backdrop.addEventListener("touchstart", onTouchStart, { passive: true });
    backdrop.addEventListener("touchmove", onTouchMove, { passive: false });
    backdrop.addEventListener("touchend", onTouchEnd);
    backdrop.addEventListener("touchcancel", onTouchEnd);

    return () => {
      backdrop.removeEventListener("touchstart", onTouchStart);
      backdrop.removeEventListener("touchmove", onTouchMove);
      backdrop.removeEventListener("touchend", onTouchEnd);
      backdrop.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onClose]);

  return (
    <div ref={backdropRef} className={className} onMouseDown={onMouseDown} onClick={onClick}>
      {children}
    </div>
  );
}
